import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Grid, Card, CardContent, Button } from '@mui/joy';
import { Calendar } from 'primereact/calendar';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Badge } from 'primereact/badge';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { RouteComponentProps } from '../../models/RouteComponent';
import * as checklistService from '../../service/enhancedChecklistService';

export default function ChecklistDashboard(props: RouteComponentProps) {

interface DashboardData {
  statistics: {
    completed: number;
    overdue: number;
    due_soon: number;
    total_pending: number;
  };
  complianceData: Array<{
    template_name: string;
    total_submissions: number;
    compliant_submissions: number;
    compliance_percentage: number;
  }>;
  recentSubmissions: Array<{
    instance_id: string;
    template_name: string;
    submission_date: string;
    overall_compliance: boolean;
    submitted_by_name: string;
    site_name: string;
  }>;
}

  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const toast = useRef<Toast>(null);

  // Determine context (client vs root admin)
  const isClientContext = props.session?.client_id && 
                         (props.session?.user_type === 'Client' || 
                          window.location.pathname.includes('/client/'));

  // Helper function to map color names to valid Joy UI color props
  const getValidColor = (colorName: string): "primary" | "neutral" | "danger" | "success" | "warning" => {
    switch (colorName) {
      case 'success':
        return 'success';
      case 'danger':
        return 'danger';
      case 'warning':
        return 'warning';
      case 'primary':
        return 'primary';
      default:
        return 'neutral';
    }
  };

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await checklistService.getChecklistDashboard({
        clientId: isClientContext ? props.session?.client_id : undefined,
        date: selectedDate.toISOString().split('T')[0]
      });
      setDashboardData(data[0]); // API returns array, take first item
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load dashboard data'
      });
    } finally {
      setLoading(false);
    }
  }, [isClientContext, props.session?.client_id, selectedDate]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const renderComplianceChart = () => {
    if (!dashboardData?.complianceData) return null;

    const chartData = {
      labels: dashboardData.complianceData.map(item => item.template_name),
      datasets: [{
        label: 'Compliance Percentage',
        data: dashboardData.complianceData.map(item => item.compliance_percentage),
        backgroundColor: [
          '#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', 
          '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF'
        ],
        hoverBackgroundColor: [
          '#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0',
          '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF'
        ]
      }]
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom' as const,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              return `${context.label}: ${context.parsed}%`;
            }
          }
        }
      }
    };

    return (
      <Card sx={{ height: '400px' }}>
        <CardContent>
          <Typography level="h4" mb={2}>Template Compliance</Typography>
          <Box sx={{ height: '300px' }}>
            <Chart type="doughnut" data={chartData} options={chartOptions} />
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderStatisticsCards = () => {
    if (!dashboardData?.statistics) return null;

    const stats = [
      {
        title: 'Completed',
        value: dashboardData.statistics.completed,
        color: 'success',
        icon: '✓'
      },
      {
        title: 'Overdue',
        value: dashboardData.statistics.overdue,
        color: 'danger',
        icon: '⚠'
      },
      {
        title: 'Due Soon',
        value: dashboardData.statistics.due_soon,
        color: 'warning',
        icon: '🕐'
      },
      {
        title: 'Total Pending',
        value: dashboardData.statistics.total_pending,
        color: 'primary',
        icon: '📋'
      }
    ];

    return (
      <Grid container spacing={3} mb={3}>
        {stats.map((stat, index) => (
          <Grid key={index} xs={12} sm={6} md={3}>
            <Card 
              variant="outlined"
              sx={{ 
                border: '2px solid',
                borderColor: `${stat.color}.400`,
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 'lg',
                  borderColor: `${stat.color}.600`,
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography level="h1" color={getValidColor(stat.color)}>
                  {stat.icon}
                </Typography>
                <Typography level="h2" color={getValidColor(stat.color)}>
                  {stat.value}
                </Typography>
                <Typography level="body-sm" color="neutral">
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderRecentSubmissions = () => {
    if (!dashboardData?.recentSubmissions) return null;

    const complianceBodyTemplate = (rowData: any) => {
      return (
        <Badge 
          value={rowData.overall_compliance ? 'Compliant' : 'Non-Compliant'}
          severity={rowData.overall_compliance ? 'success' : 'danger'}
        />
      );
    };

    const dateBodyTemplate = (rowData: any) => {
      return new Date(rowData.submission_date).toLocaleDateString();
    };

    return (
      <Card>
        <CardContent>
          <Typography level="h4" mb={2}>Recent Submissions</Typography>
          <DataTable 
            value={dashboardData.recentSubmissions}
            paginator
            rows={5}
            responsiveLayout="scroll"
            emptyMessage="No recent submissions found"
          >
            <Column field="template_name" header="Template" sortable />
            <Column field="site_name" header="Site" sortable />
            <Column field="submitted_by_name" header="Submitted By" sortable />
            <Column 
              field="submission_date" 
              header="Date" 
              body={dateBodyTemplate}
              sortable 
            />
            <Column 
              field="overall_compliance" 
              header="Compliance" 
              body={complianceBodyTemplate}
            />
          </DataTable>
        </CardContent>
      </Card>
    );
  };

  const handleTriggerEvaluation = async () => {
    try {
      await checklistService.triggerChecklistEvaluation();
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Checklist evaluation triggered successfully'
      });
      // Refresh dashboard data
      loadDashboardData();
    } catch (error) {
      console.error('Error triggering evaluation:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to trigger checklist evaluation'
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
      >
        <Typography level="h2">
          {isClientContext ? 'Client Checklist Dashboard' : 'Global Checklist Dashboard'}
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <Calendar
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.value as Date)}
            dateFormat="yy-mm-dd"
            placeholder="Select Date"
            showIcon
          />
          {!isClientContext && (
            <Button 
              variant="outlined"
              onClick={handleTriggerEvaluation}
              size="sm"
            >
              Trigger Evaluation
            </Button>
          )}
        </Box>
      </Box>

      {/* Statistics Cards */}
      {renderStatisticsCards()}

      {/* Main Content */}
      <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
        <TabPanel header="Overview">
          <Grid container spacing={3}>
            <Grid xs={12} md={6}>
              {renderComplianceChart()}
            </Grid>
            <Grid xs={12} md={6}>
              {renderRecentSubmissions()}
            </Grid>
          </Grid>
        </TabPanel>
        
        <TabPanel header="Templates">
          <Card>
            <CardContent>
              <Typography level="h4" mb={2}>Template Performance</Typography>
              {dashboardData?.complianceData && (
                <DataTable 
                  value={dashboardData.complianceData}
                  paginator
                  rows={10}
                  sortField="compliance_percentage"
                  sortOrder={-1}
                  responsiveLayout="scroll"
                >
                  <Column field="template_name" header="Template Name" sortable />
                  <Column field="total_submissions" header="Total Submissions" sortable />
                  <Column field="compliant_submissions" header="Compliant" sortable />
                  <Column 
                    field="compliance_percentage" 
                    header="Compliance %" 
                    sortable
                    body={(rowData) => `${rowData.compliance_percentage}%`}
                  />
                </DataTable>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel header="Actions">
          <Grid container spacing={3}>
            <Grid xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography level="h4" mb={2}>Quick Actions</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Button 
                      variant="outlined" 
                      onClick={() => window.location.href = '/checklists/templates'}
                    >
                      Manage Templates
                    </Button>
                    <Button 
                      variant="outlined"
                      onClick={() => window.location.href = '/checklists/instances'}
                    >
                      View All Instances
                    </Button>
                    <Button 
                      variant="outlined"
                      onClick={() => window.location.href = '/checklists/submissions'}
                    >
                      Review Submissions
                    </Button>
                    <Button 
                      variant="outlined"
                      onClick={() => window.location.href = '/checklists/follow-up-tasks'}
                    >
                      Follow-up Tasks
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography level="h4" mb={2}>Reports</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Button 
                      variant="outlined"
                      onClick={() => window.location.href = '/reports/checklist-compliance'}
                    >
                      Compliance Report
                    </Button>
                    <Button 
                      variant="outlined"
                      onClick={() => window.location.href = '/reports/checklist-performance'}
                    >
                      Performance Report
                    </Button>
                    <Button 
                      variant="outlined"
                      onClick={() => window.location.href = '/reports/overdue-checklists'}
                    >
                      Overdue Report
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </TabView>
      <Toast ref={toast} />
    </Box>
  );
}
